import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Sparkles,
  Lock,
  GraduationCap,
  Heart,
  Users,
  Eye,
  Trash2,
  Network,
  Compass,
  Phone,
  Download,
  ArrowLeft,
  Mail,
} from "lucide-react";
import Footer from "@/components/Footer";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const LAST_UPDATED = "February 5, 2026";

const ANCHORS = [
  { id: "overview", label: "Platform Security" },
  { id: "student-data", label: "Student Data Protection" },
  { id: "coppa", label: "COPPA" },
  { id: "ferpa", label: "FERPA" },
  { id: "security", label: "Authentication & Encryption" },
  { id: "responsible-ai", label: "Responsible AI" },
  { id: "rights", label: "Deletion Rights" },
  { id: "vendors", label: "Vendors" },
  { id: "citizenship", label: "Digital Citizenship" },
  { id: "nevada", label: "Nevada Alignment" },
  { id: "contact", label: "Contact" },
];

const PROMISES = [
  { icon: <Lock className="w-5 h-5" />, t: "We never sell student data — full stop." },
  { icon: <ShieldCheck className="w-5 h-5" />, t: "We do not use student data to train AI models." },
  { icon: <Heart className="w-5 h-5" />, t: "We minimize what we collect — only what's needed." },
  { icon: <Users className="w-5 h-5" />, t: "We sign district Data Privacy Agreements at no cost." },
];

export default function SecurityAndStudentDataPage() {
  useDocumentMeta({
    title: "Security & Student Data Privacy | Lemonade Stand Business Plan",
    description:
      "How The Lemonade Stand Business Plan keeps K-12 students safe online — student data privacy, COPPA + FERPA readiness, responsible AI, digital citizenship, and Nevada educational technology alignment.",
    keywords:
      "student data privacy, K-12 educational technology, FERPA, COPPA, responsible AI learning, digital citizenship, entrepreneurship education, future-ready learning, Nevada educational technology, K12 learning platform",
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-white flex flex-col">
      <div className="max-w-5xl mx-auto px-4 py-10 sm:py-14 flex-1 w-full">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm mb-6"
          data-testid="ssd-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        {/* Hero */}
        <header className="mb-10">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-3 py-1.5 rounded-full text-xs font-semibold mb-4">
            <ShieldCheck className="w-3.5 h-3.5" /> Trust, Safety &amp; Student Data
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            A Safe, Modern Place for Future-Ready Learning
          </h1>
          <p className="text-base sm:text-lg text-gray-700 max-w-3xl leading-relaxed">
            The Lemonade Stand Business Plan is a K-12 entrepreneurship and
            career-readiness platform built with student safety, privacy, and
            responsible AI at the core. This page summarizes how we keep
            student data protected, what schools and parents can expect, and
            how the platform is designed to support — not replace — great
            classroom teaching.
          </p>
          <p className="mt-3 text-sm text-gray-500">Last updated: {LAST_UPDATED}</p>

          {/* Quick promise grid */}
          <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
            {PROMISES.map((p) => (
              <div
                key={p.t}
                className="bg-white ring-1 ring-emerald-200 rounded-2xl p-4 flex items-start gap-3 shadow-sm"
              >
                <span className="bg-emerald-500 text-white w-9 h-9 rounded-xl flex items-center justify-center shrink-0">
                  {p.icon}
                </span>
                <p className="text-sm font-semibold text-gray-900 leading-snug pt-1.5">
                  {p.t}
                </p>
              </div>
            ))}
          </section>

          {/* District quick-actions */}
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href="/api/public/district-overview.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white px-5 py-2.5 rounded-full font-bold text-sm shadow transition-colors"
              data-testid="ssd-download-district-pdf"
            >
              <Download className="w-4 h-4" /> Download District One-Pager (PDF)
            </a>
            <a
              href="mailto:privacy@thewementality.com?subject=Vendor%20Review%20%2F%20Data%20Privacy%20Agreement"
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-800 px-5 py-2.5 rounded-full font-bold text-sm ring-2 ring-gray-200 hover:ring-yellow-400 transition-colors"
              data-testid="ssd-request-dpa"
            >
              <Mail className="w-4 h-4" /> Request a DPA / Vendor Form
            </a>
          </div>
        </header>

        {/* Sticky-ish anchor nav */}
        <nav
          aria-label="On this page"
          className="bg-white ring-1 ring-gray-200 rounded-2xl px-4 py-3 mb-10 sticky top-2 z-10 shadow-sm"
          data-testid="ssd-anchor-nav"
        >
          <p className="text-[10px] font-bold tracking-widest text-gray-500 uppercase mb-2">
            On this page
          </p>
          <ul className="flex flex-wrap gap-x-3 gap-y-1.5 text-xs sm:text-sm font-medium">
            {ANCHORS.map((a) => (
              <li key={a.id}>
                <a
                  href={`#${a.id}`}
                  className="text-gray-600 hover:text-yellow-700 hover:underline"
                >
                  {a.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Body */}
        <article className="prose prose-sm sm:prose-base max-w-none text-gray-800 leading-relaxed space-y-10">
          {/* 1. Platform Security Overview */}
          <section id="overview" data-testid="ssd-section-overview">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-emerald-600" /> Platform Security Overview
            </h2>
            <p>
              The Lemonade Stand Business Plan is a cloud-hosted web platform
              built with modern security practices throughout. We design the
              platform with the assumption that it will be used in K-12
              classrooms — meaning student safety, predictable behavior, and
              data minimization come before everything else.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>HTTPS / TLS 1.2+ on every connection between the student's device and our servers.</li>
              <li>HSTS enforcement so browsers refuse to downgrade to insecure HTTP.</li>
              <li>Standard browser hardening headers: clickjacking protection, MIME-sniffing protection, restrictive referrer policy, and limited browser-feature permissions.</li>
              <li>U.S.-based hosting on managed Kubernetes (Emergent Cloud) with managed MongoDB (Atlas).</li>
              <li>Rate-limiting on authentication and AI endpoints to prevent abuse.</li>
              <li>Error monitoring (Sentry) with personally identifiable information automatically redacted at the source.</li>
            </ul>
          </section>

          {/* 2. Student Data Protection */}
          <section id="student-data" data-testid="ssd-section-student-data">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Lock className="w-6 h-6 text-emerald-600" /> Student Data Protection
            </h2>
            <p>
              We follow a <strong>data minimization</strong> approach — we
              collect only what is required to deliver lessons and show
              progress, nothing more.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>What we collect:</strong> name, email, role, lesson responses, optional pitch recordings the student chooses to make, and aggregated usage counts.</li>
              <li><strong>What we don't collect:</strong> government IDs, financial data, precise location, biometric data, or advertising profiles.</li>
              <li><strong>How long we keep it:</strong> while the account is active; on deletion request, we remove records within 30 days and rotate backups within 90.</li>
              <li><strong>Who can see it:</strong> the student, their assigned educator (if any), their parent (via parent share link), and the school under FERPA "school official" terms.</li>
              <li><strong>What we never do:</strong> sell student data, share it for advertising, or use it to train external AI models.</li>
            </ul>
            <p className="mt-3">
              See the full{" "}
              <Link to="/privacy" className="text-yellow-700 underline">
                Privacy Policy
              </Link>{" "}
              for line-by-line detail.
            </p>
          </section>

          {/* 3. COPPA */}
          <section id="coppa" data-testid="ssd-section-coppa">
            <h2 className="text-2xl font-bold text-gray-900">COPPA Positioning</h2>
            <p>
              The federal Children's Online Privacy Protection Act (COPPA)
              applies to children under 13. We approach COPPA with a{" "}
              <strong>strict, school-first model</strong>:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Our public Elementary preview ("Lemonade Leaders Jr." — grades
                2–5) is designed to require <strong>no account and no data
                transmission</strong>. All progress is stored locally in the
                child's own browser.
              </li>
              <li>
                Any future student accounts under 13 will only be created with{" "}
                <strong>verifiable consent</strong> from a parent, legal
                guardian, or school acting under the FERPA "school official"
                exception.
              </li>
              <li>We do not target advertising at children or anyone else.</li>
              <li>
                Parents and schools may request access, correction, or
                deletion of a child's data at any time.
              </li>
            </ul>
          </section>

          {/* 4. FERPA */}
          <section id="ferpa" data-testid="ssd-section-ferpa">
            <h2 className="text-2xl font-bold text-gray-900">FERPA Readiness</h2>
            <p>
              When a school or district uses the platform, we operate as a{" "}
              <strong>"school official"</strong> under FERPA (34 CFR §
              99.31(a)(1)). That means:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Student education records remain under the school's direction and control.</li>
              <li>We use student data only to deliver the service the school requested.</li>
              <li>We do not redisclose student data without school authorization.</li>
              <li>Schools can request export (CSV, PDF) and deletion at any time.</li>
              <li>
                We will sign your district's Student Data Privacy Agreement —
                including SDPC/NDPA standard contracts — at no cost. Email{" "}
                <a href="mailto:privacy@thewementality.com" className="text-yellow-700 underline">
                  privacy@thewementality.com
                </a>{" "}
                to start.
              </li>
            </ul>
          </section>

          {/* 5. Authentication & Encryption */}
          <section id="security" data-testid="ssd-section-security">
            <h2 className="text-2xl font-bold text-gray-900">Authentication &amp; Encryption Practices</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Passwords are hashed using bcrypt — never stored in plain text.</li>
              <li>Sessions use signed JWT tokens with rotation and short expirations.</li>
              <li>Login endpoints are protected by per-IP and per-user rate-limiting against brute-force attempts.</li>
              <li>Admin and educator routes are role-gated server-side, not just hidden in the UI.</li>
              <li>All in-transit data uses HTTPS / TLS 1.2 or higher.</li>
              <li>At-rest data lives in managed MongoDB Atlas (U.S. region) with provider-level encryption.</li>
              <li>Internal team access is restricted, audited, and reviewed.</li>
            </ul>
          </section>

          {/* 6. Responsible AI */}
          <section
            id="responsible-ai"
            data-testid="ssd-section-responsible-ai"
            className="bg-sky-50 border-l-4 border-sky-400 rounded-r-2xl p-5 sm:p-6"
          >
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2 !mt-0">
              <Sparkles className="w-6 h-6 text-sky-600" /> Responsible AI Statement
            </h2>
            <p className="font-semibold text-gray-900">
              AI assists learning — it does not replace student thinking.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Coach, not ghostwriter.</strong> Our AI helper
                ("LemonBot") is designed to ask Socratic questions and prompt
                reflection. It does not write a student's business plan,
                pitch, or reflection answers for them.
              </li>
              <li>
                <strong>Human creativity and entrepreneurship remain
                central.</strong> Every Mini Business Plan, every pitch, and
                every problem-solving moment belongs to the student.
              </li>
              <li>
                <strong>Student safety and privacy are prioritized.</strong>{" "}
                LemonBot conversations run under OpenAI's enterprise API
                terms — student chats are not used to train any AI model.
              </li>
              <li>
                <strong>Outputs are guided.</strong> Prompts and safety
                filters are reviewed by our team; daily per-student quotas
                keep usage focused on learning, not entertainment.
              </li>
              <li>
                <strong>Educator control.</strong> Teachers and admins can
                disable LemonBot for any class with one click.
              </li>
            </ul>
          </section>

          {/* 7. Account Deletion Rights */}
          <section id="rights" data-testid="ssd-section-rights">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Trash2 className="w-6 h-6 text-rose-600" /> Account &amp; Deletion Rights
            </h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Any account holder can permanently delete their account and
                associated learning data at{" "}
                <Link to="/delete-account" className="text-yellow-700 underline">
                  /delete-account
                </Link>
                .
              </li>
              <li>
                Parents and guardians may request deletion on behalf of their
                child by emailing{" "}
                <a href="mailto:privacy@thewementality.com" className="text-yellow-700 underline">
                  privacy@thewementality.com
                </a>
                .
              </li>
              <li>
                Schools may request bulk deletion at the end of a course,
                school year, or pilot period.
              </li>
              <li>Deletions complete within 30 days; backups rotate within 90.</li>
              <li>You can download your own work (PDF) before deleting.</li>
            </ul>
          </section>

          {/* 8. Vendors */}
          <section id="vendors" data-testid="ssd-section-vendors">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Network className="w-6 h-6 text-indigo-600" /> Vendor &amp; Subprocessor Transparency
            </h2>
            <p>
              We use a small, carefully selected set of service providers to
              run the platform. The full, current list — including what data
              each handles and where it sits — lives on our{" "}
              <Link to="/subprocessors" className="text-yellow-700 underline">
                Subprocessors page
              </Link>
              . Notable highlights:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Hosting:</strong> Emergent Cloud (U.S. Kubernetes).</li>
              <li><strong>Database:</strong> MongoDB Atlas (U.S. region).</li>
              <li><strong>AI:</strong> OpenAI Enterprise API (no model training on customer data).</li>
              <li><strong>Email:</strong> SendGrid (transactional only).</li>
              <li><strong>Analytics:</strong> Privacy-respecting (PostHog + GA4) — no advertising profiles.</li>
              <li><strong>Error tracking:</strong> Sentry with PII redaction.</li>
            </ul>
          </section>

          {/* 9. Digital Citizenship */}
          <section id="citizenship" data-testid="ssd-section-citizenship">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Eye className="w-6 h-6 text-amber-600" /> Digital Citizenship &amp; Safe Technology Use
            </h2>
            <p>
              Entrepreneurship education is also an opportunity to build
              healthy digital habits. Our lessons and activities are designed
              to support — not undermine — classroom digital-citizenship work:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Modeling responsible AI use.</strong> LemonBot
                demonstrates that AI is a tool for thinking, not a shortcut
                around thinking.
              </li>
              <li>
                <strong>Healthy creator mindset.</strong> Students are guided
                to build products and ideas that respect customers and
                communities.
              </li>
              <li>
                <strong>Safe sharing.</strong> Public share links (Mini
                Business Plans, certificates) use opaque tokens — no
                personally identifiable URLs.
              </li>
              <li>
                <strong>Respectful communication.</strong> Our Terms of
                Service prohibit harassment, hate, and harmful content; abuse
                reports are reviewed quickly.
              </li>
              <li>
                <strong>Cybersecurity awareness.</strong> Students learn how
                their information is stored, what HTTPS means, and why
                strong passwords matter — through the lens of running their
                own business.
              </li>
            </ul>
          </section>

          {/* 10. Nevada Alignment */}
          <section id="nevada" data-testid="ssd-section-nevada">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Compass className="w-6 h-6 text-rose-600" /> Nevada Technology &amp; Digital Citizenship Alignment
            </h2>
            <p>
              The platform is designed to support Nevada classrooms working
              toward future-ready learning. We use the phrasing "aligned
              with" and "designed to support" intentionally — we are not a
              state-certified solution, but we have built the experience to
              fit the priorities Nevada educators tell us matter:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Built to support the <strong>Nevada Computer Science &amp;
                Integrated Technology Standards</strong> — students plan,
                problem-solve, and use technology purposefully throughout
                the journey.
              </li>
              <li>
                Aligned with widely accepted{" "}
                <strong>digital citizenship</strong> expectations — safe
                sharing, respectful communication, and balanced technology
                use.
              </li>
              <li>
                Designed with{" "}
                <strong>cybersecurity awareness</strong> in mind — students
                see, in context, how their data is collected, stored, and
                protected.
              </li>
              <li>
                Supports <strong>responsible, AI-assisted learning</strong> —
                our LemonBot model emphasizes student voice and original
                thinking.
              </li>
              <li>
                Aligned with Nevada's <strong>future-ready learning</strong>{" "}
                emphasis — career readiness, entrepreneurship, real-world
                problem solving, and adaptive technology fluency.
              </li>
              <li>
                Supports schools and districts pursuing{" "}
                <strong>CTE / Perkins V</strong> reporting through our
                CTE-metrics export tools (available to educators).
              </li>
            </ul>
            <p className="mt-3 text-sm text-gray-500 italic">
              Note: alignment statements describe how the platform was
              designed and how educators may apply it. They are not formal
              state endorsements or certifications.
            </p>
          </section>

          {/* 11. Contact */}
          <section id="contact" data-testid="ssd-section-contact">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Phone className="w-6 h-6 text-gray-700" /> Contact &amp; Inquiries
            </h2>
            <p>
              <strong>The WE Mentality</strong>
              <br />
              Las Vegas, Nevada, USA
            </p>
            <ul className="list-none pl-0 space-y-1">
              <li>
                Privacy &amp; security:{" "}
                <a href="mailto:privacy@thewementality.com" className="text-yellow-700 underline">
                  privacy@thewementality.com
                </a>
              </li>
              <li>
                Schools, districts &amp; pilots:{" "}
                <a href="mailto:hello@thewementality.com" className="text-yellow-700 underline">
                  hello@thewementality.com
                </a>
              </li>
            </ul>

            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 mt-6 not-prose">
              <p className="text-sm font-semibold text-emerald-900 mb-2">
                Related documents
              </p>
              <ul className="text-sm space-y-1.5">
                <li>
                  <Link to="/privacy" className="text-emerald-800 underline font-medium">Privacy Policy</Link>
                </li>
                <li>
                  <Link to="/terms" className="text-emerald-800 underline font-medium">Terms of Service</Link>
                </li>
                <li>
                  <Link to="/student-privacy" className="text-emerald-800 underline font-medium">Student Data Privacy Summary</Link>
                </li>
                <li>
                  <Link to="/subprocessors" className="text-emerald-800 underline font-medium">Subprocessors List</Link>
                </li>
                <li>
                  <a href="/api/public/district-overview.pdf" target="_blank" rel="noopener noreferrer" className="text-emerald-800 underline font-medium">
                    District One-Pager (PDF)
                  </a>
                </li>
              </ul>
            </div>
          </section>
        </article>
      </div>
      <Footer />
    </div>
  );
}
